import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';

interface Category {
  id: number;
  name: string;
  slug: string;
  compression_quality: number;
  max_width: number;
  output_format: string;
  description?: string;
}

interface CategoryFormData {
  name: string;
  slug: string;
  compression_level: string;
  size_preset: string;
  output_format: string;
  description: string;
}

export default function CategoryManager() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<CategoryFormData>({
    name: '',
    slug: '',
    compression_level: 'medium',
    size_preset: 'medium',
    output_format: 'webp',
    description: ''
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        'https://admin.clarityofconcept.com/api/media/categories',
        { headers: getAuthHeaders() }
      );
      setCategories(response.data);
    } catch (error) {
      toast.error('Failed to fetch categories');
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleCreate = async () => {
    if (!formData.name || !formData.slug) {
      toast.error('Name and slug are required');
      return;
    }

    const submitData = new FormData();
    submitData.append('name', formData.name);
    submitData.append('slug', formData.slug);
    submitData.append('compression_level', formData.compression_level);
    submitData.append('size_preset', formData.size_preset);
    submitData.append('output_format', formData.output_format);
    submitData.append('description', formData.description);

    try {
      await axios.post(
        'https://admin.clarityofconcept.com/api/media/categories/v2',
        submitData,
        { headers: getAuthHeaders() }
      );
      toast.success('Category created successfully');
      setIsCreating(false);
      setFormData({
        name: '',
        slug: '',
        compression_level: 'medium',
        size_preset: 'medium',
        output_format: 'webp',
        description: ''
      });
      fetchCategories();
    } catch (error) {
      toast.error('Failed to create category');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this category?')) return;

    try {
      await axios.delete(
        `https://admin.clarityofconcept.com/api/media/categories/${id}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Category deleted successfully');
      fetchCategories();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const getQualityLabel = (quality: number): string => {
    if (quality >= 85) return 'High';
    if (quality >= 70) return 'Medium';
    return 'Low';
  };

  const getSizeLabel = (width: number): string => {
    if (width >= 1600) return 'Large';
    if (width >= 1000) return 'Medium';
    return 'Small';
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Media Categories
            </h2>
            <button
              onClick={() => setIsCreating(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="w-5 h-5" />
              Add Category
            </button>
          </div>
        </div>

        {/* Create Form */}
        {isCreating && (
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Category Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
              />
              <input
                type="text"
                placeholder="Slug (e.g., map, diagram)"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
              />

              <select
                value={formData.compression_level}
                onChange={(e) => setFormData({ ...formData, compression_level: e.target.value })}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
              >
                <option value="high">High Quality (90%)</option>
                <option value="medium">Medium Quality (75%)</option>
                <option value="low">Low Quality (60%)</option>
              </select>

              <select
                value={formData.size_preset}
                onChange={(e) => setFormData({ ...formData, size_preset: e.target.value })}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
              >
                <option value="large">Large (2000px)</option>
                <option value="medium">Medium (1200px)</option>
                <option value="small">Small (800px)</option>
              </select>

              <select
                value={formData.output_format}
                onChange={(e) => setFormData({ ...formData, output_format: e.target.value })}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
              >
                <option value="webp">WebP</option>
                <option value="jpeg">JPEG</option>
                <option value="png">PNG</option>
              </select>

              <input
                type="text"
                placeholder="Description (optional)"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
              />
            </div>

            <div className="flex gap-2 mt-4">
              <button
                onClick={handleCreate}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <CheckIcon className="w-4 h-4" />
                Create
              </button>
              <button
                onClick={() => {
                  setIsCreating(false);
                  setFormData({
                    name: '',
                    slug: '',
                    compression_level: 'medium',
                    size_preset: 'medium',
                    output_format: 'webp',
                    description: ''
                  });
                }}
                className="flex items-center gap-2 px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
              >
                <XMarkIcon className="w-4 h-4" />
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Categories List */}
        <div className="p-6">
          <div className="grid gap-4">
            {categories.map((category) => (
              <div
                key={category.id}
                className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-4">
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        {category.name}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Slug: {category.slug}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded">
                        {getQualityLabel(category.compression_quality)} Quality
                      </span>
                      <span className="px-2 py-1 text-xs font-medium bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded">
                        {getSizeLabel(category.max_width)} Size
                      </span>
                      <span className="px-2 py-1 text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded">
                        {category.output_format.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  {category.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                      {category.description}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleDelete(category.id)}
                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Delete category"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}